/**
 * @fileoverview Comparação visual entre duas versões da DRE
 * @module components/dre/VersionCompare
 * 
 * Componente para comparar lado-a-lado duas versões da DRE
 * com highlighting de diferenças
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { useVersionStore } from '../../stores/versionStore';
import { useExportDRE } from '../../hooks/useExportDRE';
import { DREYear, DRECompany } from '../../types/dre';
import { formatCurrency, formatPercent } from '../../lib/dreCalculations';

interface VersionCompareProps {
  open: boolean;
  onClose: () => void;
  version1Id: string;
  version2Id: string;
}

export function VersionCompare({ open, onClose, version1Id, version2Id }: VersionCompareProps) {
  const { versions } = useVersionStore();
  const { exportComparison } = useExportDRE();

  const v1 = versions.find((v) => v.id === version1Id);
  const v2 = versions.find((v) => v.id === version2Id);

  if (!v1 || !v2) {
    return null;
  }

  const handleExport = () => {
    exportComparison(version1Id, version2Id);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Comparação de Versões</Typography>
            <Typography variant="caption" color="text.secondary">
              Comparando mudanças entre versões
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Exportar comparação">
              <IconButton onClick={handleExport} size="small">
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Headers das versões */}
          <Grid item xs={6}>
            <Paper sx={{ p: 2, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Versão {v1.versionNumber}: {v1.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(v1.createdAt, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                {v1.createdBy}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6}>
            <Paper sx={{ p: 2, bgcolor: 'success.50', border: 1, borderColor: 'success.200' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                Versão {v2.versionNumber}: {v2.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(v2.createdAt, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                {v2.createdBy}
              </Typography>
            </Paper>
          </Grid>

          {/* Comparação Sétima */}
          <Grid item xs={12}>
            <Typography variant="h6" mb={2}>
              Sétima
            </Typography>
            <CompanyComparison
              company1={v1.dreData.setima}
              company2={v2.dreData.setima}
              label1={`v${v1.versionNumber}`}
              label2={`v${v2.versionNumber}`}
            />
          </Grid>

          {/* Comparação 555 Studios */}
          <Grid item xs={12}>
            <Typography variant="h6" mb={2}>
              555 Studios
            </Typography>
            <CompanyComparison
              company1={v1.dreData.studio555}
              company2={v2.dreData.studio555}
              label1={`v${v1.versionNumber}`}
              label2={`v${v2.versionNumber}`}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

interface CompanyComparisonProps {
  company1: DRECompany;
  company2: DRECompany;
  label1: string;
  label2: string;
}

function CompanyComparison({ company1, company2, label1, label2 }: CompanyComparisonProps) {
  const years1 = company1.years.sort((a, b) => a.year - b.year);
  const years2 = company2.years.sort((a, b) => a.year - b.year);

  // Encontrar anos comuns
  const commonYears = years1
    .filter((y1) => years2.some((y2) => y2.year === y1.year))
    .map((y) => y.year);

  if (commonYears.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Nenhum ano em comum para comparar
        </Typography>
      </Paper>
    );
  }

  const fields: Array<{ label: string; field: keyof DREYear; isPercent?: boolean }> = [
    { label: 'Receita Bruta', field: 'receitaBruta' },
    { label: 'Receita Líquida', field: 'receitaLiquida' },
    { label: 'Lucro Bruto', field: 'lucroBruto' },
    { label: '% Margem Bruta', field: 'margemBruta', isPercent: true },
    { label: 'EBITDA', field: 'ebitda' },
    { label: '% Margem EBITDA', field: 'margemEbitda', isPercent: true },
    { label: 'EBIT', field: 'ebit' },
    { label: 'Lucro Líquido', field: 'lucroLiquido' },
    { label: '% Margem Líquida', field: 'margemLiquida', isPercent: true },
  ];

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Linha DRE</TableCell>
            {commonYears.map((year) => (
              <React.Fragment key={year}>
                <TableCell align="right" sx={{ bgcolor: 'primary.50', fontWeight: 'bold' }}>
                  {year} ({label1})
                </TableCell>
                <TableCell align="right" sx={{ bgcolor: 'success.50', fontWeight: 'bold' }}>
                  {year} ({label2})
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: 'grey.50', fontWeight: 'bold', minWidth: 120 }}>
                  Variação
                </TableCell>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map(({ label, field, isPercent }) => (
            <TableRow key={field} hover>
              <TableCell sx={{ fontWeight: label.startsWith('%') ? 'normal' : 'bold' }}>
                {label}
              </TableCell>
              {commonYears.map((year) => {
                const y1 = years1.find((y) => y.year === year);
                const y2 = years2.find((y) => y.year === year);

                if (!y1 || !y2) {
                  return (
                    <React.Fragment key={year}>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="center">-</TableCell>
                    </React.Fragment>
                  );
                }

                const val1 = y1[field] as number;
                const val2 = y2[field] as number;
                const delta = val1 !== 0 ? ((val2 - val1) / val1) * 100 : 0;
                const hasChange = Math.abs(delta) > 0.01;

                return (
                  <React.Fragment key={year}>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: hasChange ? 'primary.50' : 'inherit',
                      }}
                    >
                      {isPercent ? formatPercent(val1) : formatCurrency(val1)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: hasChange ? 'success.50' : 'inherit',
                        fontWeight: hasChange ? 'bold' : 'normal',
                      }}
                    >
                      {isPercent ? formatPercent(val2) : formatCurrency(val2)}
                    </TableCell>
                    <TableCell align="center">
                      {hasChange ? (
                        <DeltaChip delta={delta} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DeltaChip({ delta }: { delta: number }) {
  const isPositive = delta > 0;
  const color = isPositive ? 'success' : 'error';
  const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;

  return (
    <Chip
      icon={<Icon />}
      label={`${isPositive ? '+' : ''}${delta.toFixed(1)}%`}
      color={color}
      size="small"
      variant="outlined"
    />
  );
}
